import React from 'react';
import 'component/page/detail/DetailsPage.scss';
import { connect } from 'react-redux';
import { Box, Divider, Grid, Typography } from '@drone-js/core';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import StandardErrorPage from 'component/page/error/ActivityStandardErrorPage';
import { PageContentStatus } from 'core/type/PageContentStatus';
import DetailInViewWrapper from 'component/common/DetailInViewWrapper/DetailInViewWrapper';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/PackageOptions/PackageOptionsWrapper.selectors';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesPackageOptionSeenEventActivitiesFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesPackageOptionSeenEventActivitiesManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { useLocation } from 'react-router-dom';
import { fetchAvailability } from 'component/page/detail/store/availabilityIndex';
import { setSelectedTabOnDetailsPage } from 'component/page/detail/store/details';
import { InView } from 'react-intersection-observer';
import DateChipContainer from 'component/page/detail/DateChipContainer/DateChipContainer';
import PackageOptionContainer from 'component/page/detail/PackageOptions/PackageOptionContainer/PackageOptionContainer';
import ExploringActivitiesCarousel from 'component/page/detail/ExploringActivitiesCarousel/ExploringActivitiesCarousel';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';

type PackageOptionsWrapperProps = Props;

const PackageOptionsWrapperComponent: React.FC<PackageOptionsWrapperProps> = (props) => {
    const PACKAGE_OPTION_ELEMENT_NAME = 'activities-package-option';
    const location = useLocation();
    const { contentStatus, earliestAvailabilityDate, validAvailableOffers, validUnavailableOffers, activityTokenFromDetail, isContinueYourBookingFeature } = props;
    const locationSearch = location.search;
    const allValidOffers = validAvailableOffers.concat(validUnavailableOffers);
    const prevEarliestAvailabilityDate = React.useRef<Date | undefined>(undefined);

    const onSeen = (inView: boolean) => {
        if (inView && contentStatus === PageContentStatus.Success) {
            trackElementEvent<ActivitiesPackageOptionSeenEventActivitiesFieldsV2>(
                ActivitiesPackageOptionSeenEventActivitiesManifestV2.ids,
                { action_element_count: validAvailableOffers.length, unavailable_offer_count: validUnavailableOffers.length },
                { activity_id: props.activityId, action_element_value: allValidOffers.length.toString() },
            );
        }
    };

    React.useEffect(() => {
        if (prevEarliestAvailabilityDate.current !== earliestAvailabilityDate) {
            !isContinueYourBookingFeature && props.setSelectedDateFromQueryParams(locationSearch);
            if (activityTokenFromDetail) {
                fetchAvailability(activityTokenFromDetail, { excludePassengers: true });
            }
        }
        prevEarliestAvailabilityDate.current = earliestAvailabilityDate;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSearch, earliestAvailabilityDate]);

    if (props.isError) {
        return (
            <InView onChange={onSeen} triggerOnce={true} threshold={0.75}>
                <DetailInViewWrapper tabHref={props.href} isScrolling={props.isScrolling} setSelectedTabCallback={setSelectedTabOnDetailsPage}>
                    <Box
                        data-testid={PACKAGE_OPTION_ELEMENT_NAME}
                        data-element-name={PACKAGE_OPTION_ELEMENT_NAME}
                        py="16"
                        borderStyle="solid"
                        borderColor="base.subtle"
                        borderWidth={['1', '0', '0', '0']}
                    >
                        <Typography id={props.href} variant="body-s-strong" tag="h3">
                            {props.cms[238726]}
                        </Typography>
                        <StandardErrorPage page="details" noBorderMargin={true} contentStatus={props.contentStatus} isTargetToSearchPage={true} />
                    </Box>
                </DetailInViewWrapper>
            </InView>
        );
    }

    return (
        <InView onChange={onSeen} triggerOnce={true} threshold={0.75}>
            <DetailInViewWrapper tabHref={props.href} isScrolling={props.isScrolling} setSelectedTabCallback={setSelectedTabOnDetailsPage}>
                <Box
                    data-testid={PACKAGE_OPTION_ELEMENT_NAME}
                    data-element-name={PACKAGE_OPTION_ELEMENT_NAME}
                    py="16"
                    borderStyle="solid"
                    borderColor="base.subtle"
                    borderWidth={['1', '0', '0', '0']}
                >
                    <Typography id={props.href} variant="body-s-strong" tag="h2" tabIndex={0}>
                        {props.cms[238726]}
                    </Typography>
                    <Grid spacing="12">
                        <DateChipContainer />
                        <PackageOptionContainer activity={props.activity} isLoading={props.isLoading} isLoaded={props.isLoaded} />
                    </Grid>
                </Box>
            </DetailInViewWrapper>
            <Feature.On name={FeatureName.DetailPageRevamp}>
                <Feature.On name={FeatureName.RecommendedActivitiesOnDetailPage}>
                    <Divider mt="16" mb="32" />
                    <ExploringActivitiesCarousel />
                    <Divider />
                </Feature.On>
            </Feature.On>
        </InView>
    );
};

PackageOptionsWrapperComponent.displayName = 'PackageOptionsWrapperComponent';

const PackageOptionsWrapper = withErrorBoundary(PackageOptionsWrapperComponent);
PackageOptionsWrapper.displayName = 'PackageOptionsWrapper';

export default connect(mapStateToProps, mapDispatchToProps)(PackageOptionsWrapper);
